import { useEffect, useState } from 'react';
import {
  Heart, Copy, Check, X, Users, Share2,
  Twitter, Facebook, Linkedin, MessageCircle, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { getToken } from '../../utils/authToken';
import { BACKEND_URL, API_URL } from '../../config/api';

const API = API_URL;

// Referral Modal — extraído de Header.jsx en el refactor P3.1 (feb-2026).
// Funcionalidad y estilos idénticos al original.
const ReferralModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const [referralData, setReferralData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const txt = {
    es: {
      title: 'Tu Link de Referido',
      subtitle: 'Comparte y gana recompensas',
      referrals: 'Referidos',
      likes: 'Me gusta recibidos',
      yourLink: 'Tu enlace único',
      copyLink: 'Copiar enlace',
      copied: '¡Copiado!',
      shareOn: 'Compartir en redes',
      invite: 'Invita amigos a DamyxIA y obtén beneficios exclusivos',
    },
    en: {
      title: 'Your Referral Link',
      subtitle: 'Share and earn rewards',
      referrals: 'Referrals',
      likes: 'Likes received',
      yourLink: 'Your unique link',
      copyLink: 'Copy link',
      copied: 'Copied!',
      shareOn: 'Share on social',
      invite: 'Invite friends to DamyxIA and get exclusive benefits',
    },
  };
  const t = txt[language] || txt.es;

  useEffect(() => {
    if (isOpen) {
      loadReferralData();
    }
  }, [isOpen, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReferralData = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const userId = localStorage.getItem('userId') || 'invite';

      if (!token || token === 'guest') {
        setReferralData({
          referral_link: `${BACKEND_URL}/?ref=invite`,
          referral_count: 0,
          likes_received: 0,
        });
        setIsLoading(false);
        return;
      }

      const response = await axios.get(`${API}/referral/info?lang=${language}`, {});
      const referralCode = response.data?.referral_code || response.data?.code || userId;

      setReferralData({
        referral_link: `${BACKEND_URL}/?ref=${referralCode}`,
        referral_count: response.data?.referral_count || 0,
        likes_received: response.data?.likes_received || 0,
      });
    } catch (error) {
      console.error('Error loading referral data:', error);
      const userId = localStorage.getItem('userId') || 'invite';
      setReferralData({
        referral_link: `${BACKEND_URL}/?ref=${userId}`,
        referral_count: 0,
        likes_received: 0,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!referralData?.referral_link) return;

    const copyWithTextarea = () => {
      const textArea = document.createElement('textarea');
      textArea.value = referralData.referral_link;
      textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px;opacity:0;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch {
        document.body.removeChild(textArea);
        return false;
      }
    };

    let success = false;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(referralData.referral_link);
        success = true;
      }
    } catch {
      /* ignore */
    }
    if (!success) {
      success = copyWithTextarea();
    }
    if (success) {
      setCopied(true);
      toast.success(t.copied);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden"
        style={{ position: 'relative', zIndex: 99999 }}
      >
        {/* Header con gradiente */}
        <div className="relative px-6 py-8 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
              <Share2 className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">{t.title}</h2>
            <p className="text-white/80 text-sm mt-1">{t.subtitle}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-800/50">
          <div className="text-center p-3 bg-zinc-900 rounded-xl">
            <Users className="w-6 h-6 mx-auto text-indigo-400 mb-1" />
            <p className="text-2xl font-bold text-white">{referralData?.referral_count || 0}</p>
            <p className="text-xs text-zinc-400">{t.referrals}</p>
          </div>
          <div className="text-center p-3 bg-zinc-900 rounded-xl">
            <Heart className="w-6 h-6 mx-auto text-red-400 mb-1" />
            <p className="text-2xl font-bold text-white">{referralData?.likes_received || 0}</p>
            <p className="text-xs text-zinc-400">{t.likes}</p>
          </div>
        </div>

        {/* Referral Link */}
        <div className="p-4">
          <label className="block text-xs text-zinc-400 mb-2">{t.yourLink}</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={referralData?.referral_link || ''}
              readOnly
              className="flex-1 px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-300 font-mono truncate"
            />
            <button
              onClick={handleCopy}
              className={`px-4 py-2.5 rounded-lg font-medium transition-all ${
                copied
                  ? 'bg-green-600 text-white'
                  : 'bg-indigo-600 hover:bg-indigo-500 text-white'
              }`}
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Social Share */}
        <div className="px-4 pb-4">
          <p className="text-xs text-zinc-400 mb-3">{t.shareOn}</p>
          <div className="grid grid-cols-5 gap-2">
            {referralData?.referral_link && (
              <>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                    language === 'es'
                      ? '¡Únete a DamyxIA! Tu asistente de IA.'
                      : 'Join DamyxIA! Your AI assistant.',
                  )}&url=${encodeURIComponent(referralData.referral_link)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 p-2.5 bg-zinc-800 hover:bg-sky-600/20 rounded-xl transition-colors group"
                  title="Twitter"
                >
                  <Twitter className="w-5 h-5 text-sky-400" />
                  <span className="text-[9px] text-zinc-400 group-hover:text-sky-400">Twitter</span>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralData.referral_link)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 p-2.5 bg-zinc-800 hover:bg-blue-600/20 rounded-xl transition-colors group"
                  title="Facebook"
                >
                  <Facebook className="w-5 h-5 text-blue-500" />
                  <span className="text-[9px] text-zinc-400 group-hover:text-blue-400">Facebook</span>
                </a>
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(referralData.referral_link)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 p-2.5 bg-zinc-800 hover:bg-blue-600/20 rounded-xl transition-colors group"
                  title="LinkedIn"
                >
                  <Linkedin className="w-5 h-5 text-blue-400" />
                  <span className="text-[9px] text-zinc-400 group-hover:text-blue-400">LinkedIn</span>
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    (language === 'es'
                      ? '¡Únete a DamyxIA! Tu asistente de IA: '
                      : 'Join DamyxIA! Your AI assistant: ') + referralData.referral_link,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 p-2.5 bg-zinc-800 hover:bg-green-600/20 rounded-xl transition-colors group"
                  title="WhatsApp"
                >
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  <span className="text-[9px] text-zinc-400 group-hover:text-green-400">WhatsApp</span>
                </a>
                <a
                  href={`mailto:?subject=${encodeURIComponent(
                    language === 'es' ? '¡Únete a DamyxIA!' : 'Join DamyxIA!',
                  )}&body=${encodeURIComponent(
                    (language === 'es'
                      ? '¡Hola!\n\nTe invito a probar DamyxIA, un asistente de inteligencia artificial increíble.\n\nÚsalo gratis aquí: '
                      : 'Hi!\n\nI invite you to try DamyxIA, an amazing AI assistant.\n\nTry it free here: ') +
                      referralData.referral_link +
                      (language === 'es' ? '\n\n¡Saludos!' : '\n\nBest regards!'),
                  )}`}
                  className="flex flex-col items-center gap-1 p-2.5 bg-zinc-800 hover:bg-rose-600/20 rounded-xl transition-colors group"
                  title="Email"
                >
                  <Mail className="w-5 h-5 text-rose-400" />
                  <span className="text-[9px] text-zinc-400 group-hover:text-rose-400">Email</span>
                </a>
              </>
            )}
          </div>
          <p className="text-[10px] text-zinc-600 mt-2 text-center">
            {language === 'es'
              ? '¿Bloqueado? Clic derecho → Copiar enlace'
              : 'Blocked? Right-click → Copy link'}
          </p>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-zinc-800/30 border-t border-zinc-700">
          <p className="text-xs text-zinc-500 text-center">{t.invite}</p>
        </div>
      </div>
    </div>
  );
};

export default ReferralModal;
